'use client'

import { useState, useEffect, useRef, useCallback } from 'react'
import { Caveat } from 'next/font/google'
import toast from 'react-hot-toast'
import { PdfViewer } from '@/app/components/contracts/PdfViewer'
import { DocuSignSigning } from '@/app/components/contracts/DocuSignSigning'
import { Button } from '@/app/components/common/Button'
import { Spinner } from '@/app/components/common/Spinner'
import { logError } from '@/lib/logging'
import moment from 'moment-timezone'
import type { ContractField, ContractStatus } from '@/models/Contract'

const caveat = Caveat({ subsets: ['latin'] })

interface ContractData {
  id: string
  status: ContractStatus
  pdfUrl: string
  fields: ContractField[]
  signedAt?: string
  signedByName?: string
}

interface ContractSigningProps {
  /** The contract ID to load and display for signing */
  contractId: string
  /** Called after the contract is successfully signed */
  onComplete: () => void
  /** Called when the user clicks the back button */
  onBack: () => void
  /** Whether this contract uses DocuSign embedded signing */
  useDocuSign?: boolean
  /** One-time DocuSign signing URL (required when useDocuSign is true) */
  signingUrl?: string
}

/**
 * Client-facing contract signing UI shown during checkout.
 * Renders the PDF with interactive field overlays. Signature fields
 * are editable text inputs styled in Caveat script font. Date and
 * text fields are read-only with their auto-filled values.
 *
 * Guides the user through each signature field in order, scrolling
 * to the active field. Once all signature fields are filled, the
 * "Sign & Continue" button becomes enabled.
 */
export function ContractSigning({
  contractId,
  onComplete,
  onBack,
  useDocuSign,
  signingUrl,
}: ContractSigningProps) {
  // DocuSign flow: delegate entirely to DocuSignSigning component
  if (useDocuSign && signingUrl) {
    return (
      <DocuSignSigning
        contractId={contractId}
        signingUrl={signingUrl}
        onComplete={onComplete}
        onBack={onBack}
      />
    )
  }

  // Legacy custom signing flow below
  return <LegacyContractSigning contractId={contractId} onComplete={onComplete} onBack={onBack} />
}

/**
 * Legacy custom signing UI with PDF viewer and type-to-sign fields.
 * Used for contracts created before DocuSign integration.
 */
function LegacyContractSigning({
  contractId,
  onComplete,
  onBack,
}: {
  contractId: string
  onComplete: () => void
  onBack: () => void
}) {
  const [contract, setContract] = useState<ContractData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [signing, setSigning] = useState(false)
  const [signatureValues, setSignatureValues] = useState<Record<string, string>>({})
  const [activeFieldId, setActiveFieldId] = useState<string | null>(null)
  const fieldRefs = useRef<Record<string, HTMLInputElement | null>>({})

  const fetchContract = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await fetch(`/api/contracts/${contractId}`)
      if (!res.ok) {
        const data = (await res.json()) as { error?: string }
        throw new Error(data.error || 'Failed to load contract')
      }
      const data = (await res.json()) as ContractData
      setContract(data)

      // Initialize signature values from any pre-filled fields
      const sigVals: Record<string, string> = {}
      for (const field of data.fields) {
        if (field.type === 'signature') {
          sigVals[field.id] = field.value || ''
        }
      }
      setSignatureValues(sigVals)

      // Set initial active field to first empty signature field
      if (data.status === 'unsigned') {
        const firstEmpty = data.fields.find((f) => f.type === 'signature' && !f.value)
        if (firstEmpty) {
          setActiveFieldId(firstEmpty.id)
        }
      }
    } catch (err) {
      logError('Failed to fetch contract for signing:', err)
      const msg = err instanceof Error ? err.message : 'Failed to load contract'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }, [contractId])

  useEffect(() => {
    fetchContract()
  }, [fetchContract])

  // Scroll to active field when it changes
  useEffect(() => {
    if (activeFieldId && fieldRefs.current[activeFieldId]) {
      fieldRefs.current[activeFieldId]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
      // Small delay to let scroll finish, then focus
      const timer = setTimeout(() => {
        fieldRefs.current[activeFieldId]?.focus()
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [activeFieldId])

  function handleSignatureChange(fieldId: string, value: string) {
    setSignatureValues((prev) => ({ ...prev, [fieldId]: value }))

    // If this field now has a value, advance to the next empty signature field
    if (value.trim() && contract) {
      const sigFields = contract.fields.filter((f) => f.type === 'signature')
      const currentIdx = sigFields.findIndex((f) => f.id === fieldId)
      const nextEmpty = sigFields.find(
        (f, i) => i > currentIdx && !signatureValues[f.id]?.trim() && f.id !== fieldId
      )
      if (nextEmpty) {
        setActiveFieldId(nextEmpty.id)
      }
    }
  }

  function handleFieldFocus(fieldId: string) {
    setActiveFieldId(fieldId)
  }

  const signatureFields = contract?.fields.filter((f) => f.type === 'signature') || []
  const completedCount = signatureFields.filter((f) => signatureValues[f.id]?.trim()).length
  const allSignaturesFilled = completedCount === signatureFields.length
  const isVoided = contract?.status === 'voided'
  const isAlreadySigned = contract?.status !== 'unsigned' && !isVoided

  async function handleSign() {
    if (!contract || !allSignaturesFilled) return

    // Use the value from the first signature field as the signed name
    const firstSigField = signatureFields[0]
    if (!firstSigField) return

    const signedName = signatureValues[firstSigField.id]?.trim()
    if (!signedName) return

    try {
      setSigning(true)
      const res = await fetch(`/api/contracts/${contractId}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signedName }),
      })

      if (!res.ok) {
        const data = (await res.json()) as { error?: string }
        throw new Error(data.error || 'Failed to sign contract')
      }

      onComplete()
    } catch (err) {
      logError('Failed to sign contract:', err)
      const msg = err instanceof Error ? err.message : 'Failed to sign contract'
      toast.error(msg)
    } finally {
      setSigning(false)
    }
  }

  function renderOverlay(pageNumber: number, dimensions: { width: number; height: number }) {
    if (!contract) return null

    const pageFields = contract.fields.filter((f) => f.pageNumber === pageNumber)
    if (pageFields.length === 0) return null

    return (
      <>
        {pageFields.map((field) => {
          const pixelX = (field.x / 100) * dimensions.width
          const pixelY = (field.y / 100) * dimensions.height
          const pixelW = (field.width / 100) * dimensions.width
          const pixelH = (field.height / 100) * dimensions.height

          if (field.type === 'signature') {
            const isActive = activeFieldId === field.id
            const value = isAlreadySigned ? field.value || '' : signatureValues[field.id] || ''

            return (
              <div
                key={field.id}
                className={`absolute border-2 rounded ${
                  isActive && !isAlreadySigned
                    ? 'border-blue-500 ring-2 ring-blue-300 bg-blue-50/90'
                    : 'border-blue-400 bg-blue-50/70'
                }`}
                style={{
                  left: pixelX,
                  top: pixelY,
                  width: pixelW,
                  height: pixelH,
                }}
              >
                <input
                  ref={(el) => {
                    fieldRefs.current[field.id] = el
                  }}
                  type="text"
                  value={value}
                  onChange={(e) => handleSignatureChange(field.id, e.target.value)}
                  onFocus={() => handleFieldFocus(field.id)}
                  readOnly={isAlreadySigned}
                  placeholder={isAlreadySigned ? '' : 'Type your name'}
                  className={`w-full h-full bg-transparent px-2 outline-none ${caveat.className} ${
                    isAlreadySigned ? 'cursor-default' : ''
                  }`}
                  style={{ fontSize: Math.max(pixelH * 0.6, 14) }}
                />
              </div>
            )
          }

          if (field.type === 'date') {
            const dateValue = field.value || moment().format('MM/DD/YYYY')

            return (
              <div
                key={field.id}
                className="absolute border-2 border-green-400 bg-green-50/70 rounded flex items-center px-2"
                style={{
                  left: pixelX,
                  top: pixelY,
                  width: pixelW,
                  height: pixelH,
                }}
              >
                <span
                  className="text-green-800 truncate"
                  style={{ fontSize: Math.max(pixelH * 0.55, 12) }}
                >
                  {dateValue}
                </span>
              </div>
            )
          }

          // Text field (read-only, auto-filled)
          return (
            <div
              key={field.id}
              className="absolute border-2 border-gray-400 bg-gray-50/70 rounded flex items-center px-2"
              style={{
                left: pixelX,
                top: pixelY,
                width: pixelW,
                height: pixelH,
              }}
            >
              <span
                className="text-gray-800 truncate"
                style={{ fontSize: Math.max(pixelH * 0.55, 12) }}
              >
                {field.value || ''}
              </span>
            </div>
          )
        })}
      </>
    )
  }

  if (loading) {
    return (
      <div
        data-component-name="ContractSigning"
        className="flex flex-col items-center justify-center py-16"
      >
        <Spinner size="lg" />
        <p className="mt-4 text-gray-600">Loading contract...</p>
      </div>
    )
  }

  if (error || !contract) {
    return (
      <div
        data-component-name="ContractSigning"
        className="flex flex-col items-center justify-center py-16"
      >
        <p className="text-danger mb-4">{error || 'Contract not found'}</p>
        <Button variant="outline" onClick={onBack}>
          Go Back
        </Button>
      </div>
    )
  }

  if (isVoided) {
    return (
      <div
        data-component-name="ContractSigning"
        className="flex flex-col items-center justify-center py-16"
      >
        <p className="text-gray-800 text-lg mb-2">This contract has been voided.</p>
        <p className="text-gray-600 mb-6">Please contact your provider for further assistance.</p>
        <Button variant="outline" onClick={onBack}>
          Go Back
        </Button>
      </div>
    )
  }

  return (
    <div data-component-name="ContractSigning" className="flex flex-col flex-1 min-h-0">
      {/* Header */}
      <div className="w-full max-w-4xl mx-auto px-6 pt-6 mb-6">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">
          {isAlreadySigned ? 'Contract Signed' : 'Review & Sign Contract'}
        </h2>
        {!isAlreadySigned && (
          <p className="text-gray-600">
            Please review the contract below and type your name in each signature field.
            {signatureFields.length > 1
              ? ` There are ${signatureFields.length} signature fields to complete.`
              : ''}
          </p>
        )}
        {isAlreadySigned && contract.signedByName && (
          <p className="text-gray-600">
            Signed by {contract.signedByName}
            {contract.signedAt
              ? ` on ${new Date(contract.signedAt).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}`
              : ''}
          </p>
        )}
      </div>

      {/* Progress indicator for unsigned contracts */}
      {!isAlreadySigned && signatureFields.length > 0 && (
        <div className="w-full max-w-4xl mx-auto px-6 mb-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>
              {completedCount} of {signatureFields.length} signature
              {signatureFields.length === 1 ? '' : 's'} completed
            </span>
            <div className="flex-1 bg-gray-200 rounded-full h-2">
              <div
                className="bg-primary rounded-full h-2 transition-all duration-300"
                style={{
                  width: `${(completedCount / signatureFields.length) * 100}%`,
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* PDF with field overlays */}
      <div className="flex-1 min-h-0 overflow-y-auto px-6">
        <div className="max-w-4xl mx-auto">
          <PdfViewer pdfUrl={contract.pdfUrl} renderOverlay={renderOverlay} />
        </div>
      </div>

      {/* Action buttons */}
      <div className="w-full max-w-4xl mx-auto px-6 py-4 flex items-center justify-between border-t">
        <Button variant="outline" onClick={onBack} disabled={signing}>
          Back
        </Button>

        {!isAlreadySigned && (
          <Button variant="primary" onClick={handleSign} disabled={!allSignaturesFilled || signing}>
            {signing ? (
              <span className="flex items-center gap-2">
                <Spinner size="sm" color="text-white" />
                Signing...
              </span>
            ) : (
              'Sign & Continue'
            )}
          </Button>
        )}

        {isAlreadySigned && (
          <Button variant="primary" onClick={onComplete}>
            Continue
          </Button>
        )}
      </div>
    </div>
  )
}
